import React, { useEffect, useMemo, useRef, useState } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findTextMatches, JSON_COLORS, tokenizeJson } from '../logs.utils';

const TOKEN_CLASSES = {
  key: JSON_COLORS.key,
  string: JSON_COLORS.string,
  number: JSON_COLORS.number,
  literal: JSON_COLORS.literal,
  punctuation: JSON_COLORS.punctuation,
  text: JSON_COLORS.text,
};

// Même typographie et même géométrie pour la saisie et le rendu coloré superposés
const CODE_LAYOUT =
  'tw-m-0 tw-p-3 tw-font-mono tw-font-normal tw-text-xs tw-leading-5 tw-tracking-normal tw-whitespace-pre tw-break-normal';

/**
 * Rendu coloré du texte : chaque morceau est découpé aux limites des résultats de recherche,
 * le résultat courant est mis en évidence. Texte uniquement, aucun HTML interprété.
 */
function HighlightedCode({ text, matches, currentIndex }) {
  const tokens = useMemo(() => tokenizeJson(text), [text]);
  const parts = [];
  let matchIndex = 0;
  tokens.forEach((token) => {
    const tokenEnd = token.start + token.text.length;
    let position = token.start;
    while (position < tokenEnd) {
      while (matchIndex < matches.length && matches[matchIndex].end <= position) {
        matchIndex++;
      }
      const match = matches[matchIndex];
      const inMatch = match && match.start <= position;
      const end = inMatch
        ? Math.min(match.end, tokenEnd)
        : Math.min(match ? match.start : tokenEnd, tokenEnd);
      const content = text.slice(position, end);
      const className = TOKEN_CLASSES[token.type];
      parts.push(
        inMatch ? (
          <mark
            key={position}
            className={
              matchIndex === currentIndex
                ? JSON_COLORS.currentMatch
                : `${JSON_COLORS.match} ${className}`
            }
          >
            {content}
          </mark>
        ) : (
          <span key={position} className={className}>
            {content}
          </span>
        )
      );
      position = end;
    }
  });
  // Saut de ligne final : la dernière ligne garde la même hauteur que dans la zone de saisie
  return (
    <>
      {parts}
      {'\n'}
    </>
  );
}

/**
 * Éditeur des données envoyées : saisie libre sur un rendu coloré (mêmes couleurs que l'affichage),
 * avec recherche dans le texte (clés et valeurs). Entrée / Maj+Entrée parcourent les résultats ;
 * la recherche ne déplace jamais le curseur de saisie.
 */
export default function LogPayloadCodeEditor({
  id,
  value,
  onChange,
  invalid,
  describedBy,
}) {
  const textareaRef = useRef(null);
  const codeRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const matches = useMemo(
    () => findTextMatches(value, searchText),
    [value, searchText]
  );

  // La recherche repart du premier résultat ; une saisie qui en retire garde un index valide
  useEffect(() => {
    setCurrentIndex(0);
  }, [searchText]);
  useEffect(() => {
    if (currentIndex >= matches.length && matches.length) {
      setCurrentIndex(matches.length - 1);
    }
  }, [matches.length, currentIndex]);

  const syncScroll = () => {
    if (codeRef.current && textareaRef.current) {
      codeRef.current.scrollTop = textareaRef.current.scrollTop;
      codeRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Fait défiler la zone de saisie jusqu'au résultat (au tiers de la hauteur visible)
  const revealMatch = (match) => {
    const textarea = textareaRef.current;
    if (!textarea || !match) {
      return;
    }
    const before = value.slice(0, match.start);
    const line = before.split('\n').length - 1;
    const column = match.start - (before.lastIndexOf('\n') + 1);
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const context = document.createElement('canvas').getContext('2d');
    context.font = `${style.fontSize} ${style.fontFamily}`;
    const charWidth = context.measureText('0').width || 7;
    textarea.scrollTop = Math.max(0, line * lineHeight - textarea.clientHeight / 3);
    textarea.scrollLeft = Math.max(0, column * charWidth - textarea.clientWidth / 2);
    syncScroll();
  };

  useEffect(() => {
    revealMatch(matches[currentIndex]);
    // Seuls un changement de résultat ou de recherche font défiler (pas la saisie)
  }, [currentIndex, searchText]);

  const goTo = (step) => {
    if (!matches.length) {
      return;
    }
    const next = (currentIndex + step + matches.length) % matches.length;
    setCurrentIndex(next);
    revealMatch(matches[next]);
  };

  const onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      goTo(event.shiftKey ? -1 : 1);
    }
  };

  const hasSearch = Boolean(searchText.trim());

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <input
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Rechercher dans les données (ex. KMARRI)"
          aria-label="Rechercher dans les données à corriger"
          aria-controls={id}
          className="tw-flex-1 tw-min-w-[200px] tw-px-3 tw-py-1.5 tw-text-sm tw-rounded tw-border tw-border-solid tw-border-black/20 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-main"
        />
        <span
          className="tw-min-w-[90px] tw-text-xs tw-text-text-dark-medium-importance"
          role="status"
        >
          {hasSearch
            ? matches.length
              ? `${currentIndex + 1} / ${matches.length}`
              : 'Aucun résultat'
            : ''}
        </span>
        <button
          type="button"
          onClick={() => goTo(-1)}
          disabled={!matches.length}
          aria-label="Résultat précédent"
          title="Résultat précédent (Maj+Entrée)"
          className="tw-p-1.5 tw-bg-transparent tw-border tw-border-solid tw-border-black/20 tw-rounded tw-cursor-pointer disabled:tw-cursor-default disabled:tw-opacity-40"
        >
          <FontAwesomeIcon className="tw-size-3" icon={faChevronUp} />
        </button>
        <button
          type="button"
          onClick={() => goTo(1)}
          disabled={!matches.length}
          aria-label="Résultat suivant"
          title="Résultat suivant (Entrée)"
          className="tw-p-1.5 tw-bg-transparent tw-border tw-border-solid tw-border-black/20 tw-rounded tw-cursor-pointer disabled:tw-cursor-default disabled:tw-opacity-40"
        >
          <FontAwesomeIcon className="tw-size-3" icon={faChevronDown} />
        </button>
      </div>

      <div
        className={`tw-relative tw-h-[55vh] tw-rounded-md tw-overflow-hidden tw-border tw-border-solid ${
          JSON_COLORS.background
        } ${invalid ? 'tw-border-status-negative' : 'tw-border-black/20'}`}
      >
        <pre
          ref={codeRef}
          aria-hidden="true"
          className={`${CODE_LAYOUT} tw-absolute tw-inset-0 tw-overflow-hidden tw-pointer-events-none`}
        >
          <HighlightedCode
            text={value}
            matches={matches}
            currentIndex={currentIndex}
          />
        </pre>
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onScroll={syncScroll}
          spellCheck="false"
          wrap="off"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={`${CODE_LAYOUT} tw-absolute tw-inset-0 tw-w-full tw-h-full tw-box-border tw-resize-none tw-overflow-auto tw-bg-transparent tw-border-0 tw-text-transparent tw-caret-white selection:tw-bg-blue-300/40 focus:tw-outline-none`}
        />
      </div>
    </div>
  );
}
